import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Tag } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { translations } from '../translations/translations';
import '../styles/BlogPost.css';

function formatDate(dateStr, language) {
    try {
        return new Date(dateStr).toLocaleDateString(
            language === 'tr' ? 'tr-TR' : 'en-US',
            { year: 'numeric', month: 'long', day: 'numeric' }
        );
    } catch {
        return dateStr;
    }
}

function ReadingProgress() {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        function handleScroll() {
            const el = document.documentElement;
            const scrollTop = el.scrollTop || document.body.scrollTop;
            const scrollHeight = el.scrollHeight - el.clientHeight;
            setProgress(scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0);
        }
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="reading-progress" aria-hidden="true">
            <div className="reading-progress__bar" style={{ width: `${progress}%` }} />
        </div>
    );
}

// Regex: matches ![alt text](image-path)
const IMAGE_REGEX = /^!\[([^\]]*)\]\(([^)]+)\)$/;

function renderContent(content, publicUrl) {
    const paragraphs = content.split('\n\n');
    return paragraphs.map((block, i) => {
        const trimmed = block.trim();

        // ── Inline image: ![alt](src)
        const imgMatch = trimmed.match(IMAGE_REGEX);
        if (imgMatch) {
            const [, alt, src] = imgMatch;
            const resolvedSrc = src.startsWith('http') ? src : `${publicUrl}${src}`;
            return (
                <figure key={i} className="blog-post__figure">
                    <img
                        src={resolvedSrc}
                        alt={alt}
                        className="blog-post__image"
                        loading="lazy"
                    />
                    {alt && <figcaption className="blog-post__caption">{alt}</figcaption>}
                </figure>
            );
        }

        // ── Section heading: **standalone bold line**
        if (trimmed.startsWith('**') && trimmed.endsWith('**') && !trimmed.slice(2, -2).includes('\n')) {
            return (
                <h3 key={i} className="blog-post__section-heading">
                    {trimmed.slice(2, -2)}
                </h3>
            );
        }

        // ── Normal paragraph with inline **bold**
        return (
            <p
                key={i}
                className="blog-post__paragraph"
                dangerouslySetInnerHTML={{
                    __html: block.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'),
                }}
            />
        );
    });
}


export default function BlogPostPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { language } = useAppContext();
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    // t follows the post's own language once loaded; fall back to app language while loading
    const postLang = post?.lang || language;
    const t = translations[postLang];

    useEffect(() => {
        window.scrollTo(0, 0);
        fetch(`${process.env.PUBLIC_URL}/blog/posts.json`)
            .then((r) => r.json())
            .then((data) => {
                const found = data.find((p) => p.id === id);
                if (found) setPost(found);
                else setNotFound(true);
            })
            .catch(() => setNotFound(true));
    }, [id]);

    if (notFound) {
        return (
            <div className="blog-post-page blog-post-page--notfound">
                <p>{postLang === 'tr' ? 'Yazı bulunamadı.' : 'Post not found.'}</p>
                <Link to="/blog" className="blog-post-back">{postLang === 'tr' ? 'Geri dön' : 'Go back'}</Link>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="blog-post-page blog-post-page--loading">
                <div className="blog-post-loading-shimmer" />
            </div>
        );
    }

    return (
        <div className="blog-post-page">
            <ReadingProgress />

            {/* ── Minimal Header ── */}
            <header className="blog-post-header">
                <div className="blog-post-header__inner">
                    <button className="blog-post-back" onClick={() => navigate('/blog')} aria-label="Back to blog">
                        <ArrowLeft size={15} />
                        <span>{postLang === 'tr' ? 'Blog' : 'Blog'}</span>
                    </button>
                    <img
                        src={`${process.env.PUBLIC_URL}/favicon.svg`}
                        alt="Mert Özkaya"
                        width="36"
                        height="36"
                        style={{ display: 'block' }}
                    />
                </div>
            </header>

            {/* ── Article ── */}
            <motion.article
                className="blog-post-article"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
                {/* Meta */}
                <div className="blog-post__meta">
                    <span className="blog-post__category">
                        <Tag size={11} />
                        {post.category}
                    </span>
                    <span className="blog-post__date">
                        <Calendar size={11} />
                        {formatDate(post.date, language)}
                    </span>
                    <span className="blog-post__read-time">
                        <Clock size={11} />
                        {post.readTime}
                    </span>
                </div>

                {/* Title */}
                <h1 className="blog-post__title">{post.title}</h1>

                {/* Cover image (optional) */}
                {post.coverImage && (
                    <figure className="blog-post__cover">
                        <img
                            src={post.coverImage.startsWith('http') ? post.coverImage : `${process.env.PUBLIC_URL}${post.coverImage}`}
                            alt={post.title}
                            className="blog-post__cover-img"
                        />
                    </figure>
                )}

                {/* Divider */}
                <div className="blog-post__divider" />

                {/* Content */}
                <div className="blog-post__content">
                    {renderContent(post.content, process.env.PUBLIC_URL)}
                </div>

                {/* Footer */}
                <div className="blog-post__footer">
                    <button className="blog-post__footer-back" onClick={() => navigate('/blog')}>
                        <ArrowLeft size={14} />
                        {t.blog.allPosts || (postLang === 'tr' ? 'Tüm Yazılar' : 'All Posts')}
                    </button>
                </div>
            </motion.article>
        </div>
    );
}
